import { useQueries } from '@tanstack/react-query';
import { getMatchStats, type MatchStatsResponse } from '@/api/feed/getFeedMatchStats';
import type { Question } from '@/api/questions/getQuestions';

/**
 * Hook to batch fetch match statistics for multiple questions
 *
 * Deduplicates API calls for questions that share the same match ID.
 * Returns a map of matchId -> MatchStatsResponse for easy lookup.
 */
export function useMatchStatsForQuestions(questions: Question[]) {
  // Extract unique match IDs from questions
  const matchIds = Array.from(
    new Set(
      questions
        .map(q => {
          // Check fixture_external_id (from API) and question_metadata for match ID
          const questionMetadata = q.question_metadata as
            | { grading?: { sportMatchId?: string } }
            | null
            | undefined;
          const matchId = q.fixture_external_id || questionMetadata?.grading?.sportMatchId;
          return matchId;
        })
        .filter((id): id is string => !!id)
    )
  );

  // Use useQueries to fetch all match stats in parallel
  const queries = useQueries({
    queries: matchIds.map(matchId => ({
      queryKey: ['feed', 'matches', matchId, 'statistics'],
      queryFn: () => getMatchStats(matchId),
      enabled: !!matchId,
      staleTime: 30 * 1000, // 30 seconds - stats can change during live matches
    })),
  });

  // Build a map of matchId -> stats for easy lookup
  const statsMap = new Map<string, MatchStatsResponse>();

  queries.forEach((query, index) => {
    const matchId = matchIds[index];
    if (matchId && query.data) {
      statsMap.set(matchId, query.data);
    }
  });

  // Calculate loading and error states
  const isLoading = queries.some(q => q.isLoading);
  const hasError = queries.some(q => q.isError);
  const errors = queries
    .map((q, index) => (q.error ? { matchId: matchIds[index], error: q.error } : null))
    .filter((e): e is { matchId: string; error: Error } => !!e);

  return {
    statsMap,
    isLoading,
    hasError,
    errors,
  };
}
